import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { History, RotateCw, Megaphone, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const money = v => (v ? `$${Math.round(v).toLocaleString()}` : 'no price');
const ago = iso => {
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `${Math.max(1, m)}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

export function SearchHistory({ refreshKey, onRerun, onReblast }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => API.get('/property-searches').then(r => setItems(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const del = async (id) => {
    try { await API.delete(`/property-searches/${id}`); setItems(x => x.filter(s => s.id !== id)); } catch { toast.error('Could not delete'); }
  };
  if (!items.length) return null;
  return (
    <div data-testid="pm-history">
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900" data-testid="pm-history-toggle">
        <History className="h-3.5 w-3.5" /> Recent searches ({items.length})<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="pm-history-list">
          {items.map(s => (
            <div key={s.id} className="flex min-w-0 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2" data-testid={`pm-history-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{s.property.property_address}</p>
                <p className="truncate text-[11px] text-gray-500">{[s.property.property_city, s.property.property_zip].filter(Boolean).join(' ')} · {money(s.property.asking_price)} · {s.result_count} buyers · {ago(s.last_run_at)}</p>
              </div>
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onRerun(s.property)} data-testid={`pm-history-rerun-${s.id}`} title="Re-run search"><RotateCw className="h-3.5 w-3.5" /></Button>
              <Button size="sm" className="h-7 px-2 bg-[#0073ea] hover:bg-[#0060c2] text-white" onClick={() => onReblast(s.property)} data-testid={`pm-history-reblast-${s.id}`} title="Re-run and create campaign"><Megaphone className="h-3.5 w-3.5 mr-1" />Re-blast</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-gray-400 hover:text-red-400" onClick={() => del(s.id)} data-testid={`pm-history-delete-${s.id}`} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
